import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContainer, PageContent } from '@/components/ui/page-layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import InsightsChartsTab from './insights/InsightsChartsTab';
import InsightsNetworkTab from './insights/InsightsNetworkTab';
import { useAuth } from '../auth/AuthContext';
import { hasAnyRole, NETWORK_ROLES } from '../auth/roles';

type View = 'charts' | 'network';

function readView(params: URLSearchParams): View {
  return params.get('view') === 'network' ? 'network' : 'charts';
}

export default function InsightsPage() {
  const { t } = useTranslation('menu');
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // The network view is role-gated; charts is open to any signed-in user. Fall back to
  // charts if a forbidden network view is requested via URL (the backend 403s it anyway).
  const canNetwork = hasAnyRole(user?.roles, NETWORK_ROLES);
  const view: View = readView(searchParams) === 'network' && canNetwork ? 'network' : 'charts';

  const handleTabChange = (next: string) => {
    const nextView = next === 'network' ? 'network' : 'charts';
    const params = new URLSearchParams(searchParams);
    params.set('view', nextView);
    setSearchParams(params);
  };

  return (
    <PageContainer>
      <PageContent>
        <Tabs value={view} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="charts">{t('tab.charts')}</TabsTrigger>
            {canNetwork && <TabsTrigger value="network">{t('tab.network')}</TabsTrigger>}
          </TabsList>
          <TabsContent value="charts" className="mt-6">
            {view === 'charts' && <InsightsChartsTab />}
          </TabsContent>
          {canNetwork && (
            <TabsContent value="network" className="mt-6">
              {view === 'network' && <InsightsNetworkTab />}
            </TabsContent>
          )}
        </Tabs>
      </PageContent>
    </PageContainer>
  );
}
